// Instalación: npm install html5-qrcode
import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Tesseract from 'tesseract.js';

export default function QrDniReader({ onResult, onClose }) {
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('qr'); // 'qr' o 'mrz'
  const [processing, setProcessing] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // Unique ID for each instance to avoid video duplication
  const uniqueIdRef = useRef(`qr-dni-reader-${Math.random().toString(36).substr(2, 9)}`);
  const qrRef = useRef();
  const html5QrCodeRef = useRef(null);
  // Responsive scan box size
  const [scanBox, setScanBox] = useState({ size: 350 });

  // Ajustar tamaño del recuadro según pantalla
  React.useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      if (w < 400) setScanBox({ size: w - 32 });
      else if (w < 600) setScanBox({ size: 320 });
      else setScanBox({ size: 350 });
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (mode !== 'qr') return;
    let isUnmounted = false;
    // Cleanup function to stop and clear the scanner and remove the container
    const cleanup = () => {
      if (html5QrCodeRef.current) {
        try {
          if (
            typeof html5QrCodeRef.current.getState === 'function' &&
            html5QrCodeRef.current.getState() === 2 &&
            typeof html5QrCodeRef.current.stop === 'function'
          ) {
            const stopResult = html5QrCodeRef.current.stop();
            if (stopResult && typeof stopResult === 'object' && typeof stopResult.catch === 'function') stopResult.catch(() => {});
          }
          if (typeof html5QrCodeRef.current.clear === 'function') {
            const clearResult = html5QrCodeRef.current.clear();
            if (clearResult && typeof clearResult === 'object' && typeof clearResult.catch === 'function') clearResult.catch(() => {});
          }
        } catch (e) {}
        html5QrCodeRef.current = null;
      }
      if (qrRef.current && qrRef.current.parentNode) {
        qrRef.current.parentNode.removeChild(qrRef.current);
      }
    };

    cleanup(); // Clean up before starting

    // Create a new container div for the scanner
    const container = document.createElement('div');
    container.id = uniqueIdRef.current;
    container.style.width = '350px';
    container.style.height = '350px';
    qrRef.current = container;
    const parent = document.getElementById('qr-dni-parent');
    if (parent) {
      parent.appendChild(container);
    }

    const config = { fps: 10, qrbox: scanBox.size };
    const html5QrCode = new Html5Qrcode(uniqueIdRef.current);
    html5QrCodeRef.current = html5QrCode;
    html5QrCode.start(
      { facingMode: 'environment' },
      config,
      (decodedText) => {
        if (decodedText && !isUnmounted) {
          // El QR del DNI argentino nuevo tiene más de 8 partes y empieza con "DNI"
          const partes = decodedText.split('@');
          if (partes.length > 8 && (partes[0] === 'DNI' || partes[0] === '')) {
            const dni = partes[4];
            const apellido = partes[1];
            const nombre = partes[2];
            const sexo = partes[3];
            const fechaNacimiento = partes[6];
            onResult({ dni, apellido, nombre, sexo, fechaNacimiento });
            // Parar el scanner
            if (
              html5QrCode &&
              typeof html5QrCode.getState === 'function' &&
              html5QrCode.getState() === 2 &&
              typeof html5QrCode.stop === 'function'
            ) {
              const stopResult = html5QrCode.stop();
              if (stopResult && typeof stopResult === 'object' && typeof stopResult.catch === 'function') stopResult.catch(() => {});
            }
          } else {
            setError('No se reconoce el QR. Usá el código del dorso del DNI argentino nuevo.');
          }
        }
      },
      (err) => {
        if (err && err.name === 'NotAllowedError') setError('Permiso denegado para la cámara');
      }
    ).catch((err) => {
      setError('No se pudo iniciar la cámara');
    });
    return () => {
      isUnmounted = true;
      cleanup();
    };
  }, [onResult, mode, scanBox.size]);


  // MRZ OCR desde video
  useEffect(() => {
    if (mode !== 'mrz') return;
    setError(null);
    // Iniciar cámara para MRZ
    const startVideo = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        streamRef.current = stream;
      } catch (e) {
        setError('No se pudo acceder a la cámara para MRZ');
      }
    };
    startVideo();
    return () => {
      // Detener cámara
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [mode]);

  const handleMrzCapture = async () => {
    setError(null);
    setProcessing(true);
    try {
      const video = videoRef.current;
      if (!video) throw new Error('No hay video');
      // Capturar frame
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      // Recortar área MRZ: banda central inferior (22% alto, 90% ancho, 8% bottom)
      const cropH = Math.round(vh * 0.22);
      const cropW = Math.round(vw * 0.90);
      const cropX = Math.round(vw * 0.05);
      const cropY = Math.round(vh * 0.92 - cropH);
      // Crear canvas recortado
      const canvas = document.createElement('canvas');
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      // Mejorar contraste (blanco y negro)
      const imgData = ctx.getImageData(0, 0, cropW, cropH);
      for (let i = 0; i < imgData.data.length; i += 4) {
        const avg = (imgData.data[i] + imgData.data[i+1] + imgData.data[i+2]) / 3;
        const v = avg > 128 ? 255 : 0;
        imgData.data[i] = imgData.data[i+1] = imgData.data[i+2] = v;
      }
      ctx.putImageData(imgData, 0, 0);
      // Escalar x2 para OCR
      const scaleCanvas = document.createElement('canvas');
      scaleCanvas.width = cropW * 2;
      scaleCanvas.height = cropH * 2;
      scaleCanvas.getContext('2d').drawImage(canvas, 0, 0, cropW * 2, cropH * 2);
      const dataUrl = scaleCanvas.toDataURL('image/jpeg');
      const { data: { text } } = await Tesseract.recognize(dataUrl, 'eng', { tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<', });
      // Buscar líneas MRZ (2 líneas, 44 caracteres cada una)
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 35);
      if (lines.length >= 2) {
        // Parseo simple MRZ tipo ID-1
        const l1 = lines[0].replace(/ /g, '');
        const l2 = lines[1].replace(/ /g, '');
        // DNI: últimos 8 dígitos antes del primer <
        const dniMatch = l1.match(/([0-9]{7,8})<+/);
        // Apellido y nombre
        const nameMatch = l2.match(/([A-Z<]+)<<([A-Z<]+)/);
        // Fecha nacimiento: 7-12 (YYMMDD)
        const birth = l1.substr(13, 6);
        // Sexo: posición 20
        const sexo = l1[20] === 'M' ? 'M' : (l1[20] === 'F' ? 'F' : 'X');
        let nombre = '', apellido = '';
        if (nameMatch) {
          apellido = nameMatch[1].replace(/</g, ' ');
          nombre = nameMatch[2].replace(/</g, ' ');
        }
        let fechaNacimiento = '';
        if (birth.length === 6) {
          const y = birth.substr(0,2);
          const m = birth.substr(2,2);
          const d = birth.substr(4,2);
          fechaNacimiento = `19${y}-${m}-${d}`;
        }
        if (dniMatch) {
          onResult({ dni: dniMatch[1], apellido, nombre, sexo, fechaNacimiento });
        } else {
          setError('No se pudo extraer el DNI de la MRZ.');
        }
      } else {
        setError('No se detectó MRZ válida.');
      }
    } catch (e) {
      setError('Error de OCR: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, textAlign: 'center', position: 'relative', maxWidth: 420, margin: '0 auto' }}>
      <h5 style={{ fontSize: 18, marginBottom: 8 }}>Escanear DNI argentino</h5>
      <div style={{ marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => setMode(mode === 'qr' ? 'mrz' : 'qr')}
          style={{
            background: mode === 'qr' ? '#1976d2' : '#00e676',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '6px 14px',
            fontSize: 15,
            marginBottom: 4,
            marginRight: 4,
            cursor: 'pointer',
          }}
        >
          {mode === 'qr' ? 'Usar cámara para MRZ' : 'Usar cámara para QR'}
        </button>
      </div>
      {mode === 'qr' ? (
        <div id="qr-dni-parent" style={{ position: 'relative', width: scanBox.size, height: scanBox.size, margin: '0 auto', maxWidth: '100vw' }}>
          {/* Overlay visual para apuntar el QR */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: scanBox.size,
            height: scanBox.size,
            pointerEvents: 'none',
            boxSizing: 'border-box',
            border: '3px solid #00e676',
            borderRadius: 18,
            boxShadow: '0 0 0 4px rgba(0,230,118,0.15)'
          }} />
        </div>
      ) : (
        <div style={{ margin: '0 auto', maxWidth: 420, padding: 8, position: 'relative' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 400, margin: '0 auto' }}>
            <video
              ref={videoRef}
              style={{ width: '100%', maxWidth: 400, borderRadius: 10, border: '2px solid #00e676', marginBottom: 8 }}
              autoPlay
              playsInline
              muted
            />
            {/* Overlay guía para MRZ */}
            <div style={{
              position: 'absolute',
              left: '5%',
              width: '90%',
              height: '22%',
              bottom: '8%',
              border: '2.5px dashed #1976d2',
              borderRadius: 8,
              pointerEvents: 'none',
              boxSizing: 'border-box',
              zIndex: 2
            }} />
            <div style={{
              position: 'absolute',
              left: '5%',
              width: '90%',
              height: '22%',
              bottom: '8%',
              color: '#1976d2',
              fontWeight: 600,
              fontSize: 13,
              textAlign: 'center',
              zIndex: 3,
              pointerEvents: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              Alineá la MRZ (banda OCR) dentro del recuadro azul
            </div>
          </div>
          <button
            type="button"
            onClick={handleMrzCapture}
            disabled={processing}
            style={{
              background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 18px', fontSize: 16, marginBottom: 8, cursor: 'pointer', width: '100%', maxWidth: 320
            }}
          >
            {processing ? 'Procesando...' : 'Escanear MRZ ahora'}
          </button>
          <div style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>
            Apuntá la cámara a la banda OCR (MRZ) del dorso del DNI y alineala dentro del recuadro azul.<br/>
            El texto debe verse nítido y sin reflejos.<br/>
            <b>Funciona mejor con fondo blanco y buena luz.</b>
          </div>
        </div>
      )}
      {mode === 'qr' && (
        <div style={{ color: '#888', fontSize: 13, marginTop: 8, marginBottom: 4 }}>
          <b>Usá el QR del dorso del DNI argentino nuevo.</b>
        </div>
      )}
      {processing && <div style={{ color: '#1976d2', marginTop: 8 }}>Procesando imagen...</div>}
      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
      {mode === 'qr' ? (
        <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>
          Consejo: Asegurate de que el QR esté bien enfocado y dentro del recuadro verde. Si la cámara no enfoca, alejá y acercá el DNI suavemente.<br/>
          Evitá reflejos y usa buena luz.<br/>
          Si no lee, probá con otro teléfono o cámara.
        </div>
      ) : (
        <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>
          Consejo: La MRZ son las dos líneas de texto con &lt; en el dorso del DNI.<br/>
          Si la foto sale borrosa, intentá de nuevo.<br/>
          Si no lee, probá con otra cámara o escaneá en un fondo blanco.
        </div>
      )}
      <button
        onClick={() => {
          // Apagar cámara al cerrar y limpiar DOM
          if (html5QrCodeRef.current) {
            try {
              if (
                typeof html5QrCodeRef.current.getState === 'function' &&
                html5QrCodeRef.current.getState() === 2 &&
                typeof html5QrCodeRef.current.stop === 'function'
              ) {
                const stopResult = html5QrCodeRef.current.stop();
                if (stopResult && typeof stopResult === 'object' && typeof stopResult.catch === 'function') stopResult.catch(() => {});
              }
              if (typeof html5QrCodeRef.current.clear === 'function') {
                const clearResult = html5QrCodeRef.current.clear();
                if (clearResult && typeof clearResult === 'object' && typeof clearResult.catch === 'function') clearResult.catch(() => {});
              }
            } catch (e) {}
            html5QrCodeRef.current = null;
          }
          if (qrRef.current) qrRef.current.innerHTML = '';
          onClose();
        }}
        style={{ marginTop: 12, width: '100%', maxWidth: 320, fontSize: 16, padding: 10, borderRadius: 8 }}
      >
        Cerrar
      </button>
    </div>
  );
}


